import * as UI from "@iyk/music-ui"
import { AppLink, AppNavLink, type AppRoute } from "@iyk/react-router/client"
import { AppRoutes } from "../../../AppRoutes"
import { ShellContext } from "./ShellContext"

const Root = ({ isSignedIn }: { isSignedIn: boolean }) => {
  const { isMenuOpen, setIsMenuOpen } = ShellContext.useContext()

  return (
    <UI.Primitive.Dialog.Root open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      {/** Desktop Menu */}
      <nav className="hidden h-screen lg:flex flex-col justify-center p-4">
        <MainLinks isSignedIn={isSignedIn} />
      </nav>

      {/** Mobile Menu */}
      <UI.Primitive.Dialog.Portal>
        <UI.Primitive.Dialog.Popup
          className={UI.cx(
            "lg:hidden fixed z-50 inset-0 flex flex-col transition duration-300",
            "bg-[color-mix(in_srgb,var(--background)_85%,transparent)] dark:bg-[color-mix(in_srgb,var(--app-background-dark)_85%,transparent)] backdrop-blur-sm",
            "[&[data-ending-style]]:opacity-0 [&[data-starting-style]]:opacity-0"
          )}
        >
          <div className="w-full h-[var(--shell-header-height-mobile)] px-4 flex justify-between items-center gap-2">
            <AppLink to={AppRoutes.home}>
              <UI.Icon.IykLogo className="size-7" />
            </AppLink>

            <UI.Primitive.Dialog.Close className={UI.classForButton({ size: "sm", kind: "ghost" })}>
              <UI.Icon.Close className="text-gray-12" />
            </UI.Primitive.Dialog.Close>
          </div>

          <div className="py-2 px-3">
            <MainLinks isSignedIn={isSignedIn} />
          </div>
        </UI.Primitive.Dialog.Popup>
      </UI.Primitive.Dialog.Portal>
    </UI.Primitive.Dialog.Root>
  )
}

const Trigger = ({ className }: { className?: string }) => {
  const { setIsMenuOpen } = ShellContext.useContext()

  return (
    <UI.Button
      onClick={() => setIsMenuOpen(true)}
      type="button"
      kind="ghost"
      size="sm"
      className={UI.cx("text-gray-12", className)}
    >
      <UI.Icon.Menu />
    </UI.Button>
  )
}

const MainLinks = ({ isSignedIn }: { isSignedIn: boolean }) => (
  <nav className="flex flex-col gap-1">
    <NavLink to={AppRoutes.home} title="Home" icon={UI.MusicIcon.Home} />

    {isSignedIn && <NavLink to={AppRoutes.myWorlds} title="My Worlds" icon={UI.MusicIcon.Stack} />}
  </nav>
)

const NavLink = ({
  to,
  title,
  icon: Icon,
  end
}: {
  to: AppRoute
  title: string
  icon: React.ComponentType<UI.MusicIcon.Props>
  end?: boolean
}) => {
  const { setIsMenuOpen } = ShellContext.useContext()

  return (
    <AppNavLink
      onClick={() => setIsMenuOpen(false)}
      end={end}
      to={to}
      className={({ isActive }) =>
        UI.cx(
          "p-1.5 px-2 flex items-center gap-3 rounded-md sm:max-w-64",
          isActive ? "bg-gray-3" : "hover:bg-gray-2 focus-visible:bg-gray-3"
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon kind={isActive ? "solid" : "outline"} />
          {title}
        </>
      )}
    </AppNavLink>
  )
}

export const ShellMenu = {
  Root,
  Trigger
}
